import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { SessionUser } from '../../lib/auth';
import { RequireSession } from '../RequireSession';
import { AppHeader } from '../../components/AppHeader';
import { api } from '../../lib/api';
import { usePageMeta } from '../../lib/meta';
import {
  parsePrefs, parseMeals, groupByDay, weeklySeries,
  type DaySummary, type Meal, type Prefs, type TrendPoint,
} from '../../lib/diary';

type BackupView = {
  version: number;
  schemaV: number;
  payload: unknown;
  deviceId?: string;
  updatedAt: string;
};

type Loaded = {
  prefs: Prefs;
  days: DaySummary[];
  trend: TrendPoint[];
  updatedAt: string;
};

export function Dashboard() {
  usePageMeta('Your diary — shotmeal');
  return <RequireSession>{(user) => <DashboardInner user={user} />}</RequireSession>;
}

function DashboardInner({ user }: { user: SessionUser }) {
  const [state, setState] = useState<'loading' | 'empty' | 'ready' | 'error'>('loading');
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = await api<BackupView | undefined>('/api/sync');
        if (cancelled) return;
        if (!b || !b.payload) { setState('empty'); return; }
        const meals = parseMeals(b.payload);
        if (meals.length === 0) { setState('empty'); return; }
        setData({
          prefs: parsePrefs(b.payload),
          days: groupByDay(meals),
          trend: weeklySeries(meals),
          updatedAt: b.updatedAt,
        });
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load your diary');
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const links = user.role === 'admin' ? [{ href: '/admin', label: 'Admin' }] : [];
  const goal = data?.prefs.goal;
  const today = data?.days.find((d) => d.label === 'Today');
  const history = data?.days.filter((d) => d.label !== 'Today') ?? [];

  return (
    <main class="page-in" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppHeader user={user} links={links} />

      <div style={{ flex: 1, padding: 24, maxWidth: 760, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {state === 'loading' && <Muted>Loading your diary…</Muted>}
        {state === 'error' && <p style={{ color: 'var(--wine)' }}>{error}</p>}

        {state === 'empty' && (
          <Card>
            <h1 class="wordmark" style={{ margin: 0, fontSize: 30, letterSpacing: '-0.02em' }}>
              Nothing here yet
            </h1>
            <p style={{ marginTop: 10, color: 'var(--ink-2)', lineHeight: 1.6 }}>
              Your food diary will appear here once you’ve logged meals in the
              shotmeal app and it has synced. Open the app on your phone, make
              sure you’re signed in with <strong>{user.email}</strong>, and your
              entries will show up on this page.
            </p>
          </Card>
        )}

        {state === 'ready' && data && (
          <>
            <TodayCard day={today} goal={goal} />
            <TrendCard trend={data.trend} goal={goal} />
            <History days={history} goal={goal} />
            <p style={{ marginTop: 20, fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
              Read-only view of your synced diary · last synced {fmtAgo(data.updatedAt)} · photos stay on your phone
            </p>
          </>
        )}
      </div>
    </main>
  );
}

function TodayCard({ day, goal }: { day?: DaySummary; goal?: number }) {
  const total = day?.total ?? 0;
  const pct = goal ? Math.round((total / goal) * 100) : null;
  return (
    <section
      style={{
        background: 'var(--card)', borderRadius: 24, padding: 28,
        boxShadow: 'var(--shadow-card)', marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h1 class="wordmark" style={{ margin: 0, fontSize: 28, letterSpacing: '-0.02em' }}>Today</h1>
        <div style={{ fontSize: 15, color: 'var(--ink-2)' }}>
          <strong style={{ color: 'var(--ink)', fontSize: 22 }}>{total.toLocaleString()}</strong>
          {goal ? <> / {goal.toLocaleString()} kcal</> : <> kcal</>}
        </div>
      </div>

      {goal && (
        <div style={{ marginTop: 14 }}>
          <div style={{ height: 10, borderRadius: 999, background: 'var(--surface-2)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', width: `${Math.min(100, pct ?? 0)}%`,
                background: (pct ?? 0) > 100 ? 'var(--wine)' : 'var(--gradient)',
                borderRadius: 999, transition: 'width 300ms ease',
              }}
            />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
            {pct}% of goal{(pct ?? 0) > 100 ? ' — over' : `, ${(goal - total).toLocaleString()} kcal left`}
          </div>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {day && day.meals.length > 0
          ? day.meals.map((m) => <MealRow key={m.uid} meal={m} />)
          : <Muted>No meals logged today.</Muted>}
      </div>
    </section>
  );
}

function TrendCard({ trend, goal }: { trend: TrendPoint[]; goal?: number }) {
  const max = Math.max(goal ?? 0, ...trend.map((t) => t.total), 1);
  return (
    <section
      style={{
        background: 'var(--card)', borderRadius: 20, padding: 22,
        boxShadow: '0 0 0 1px var(--line)', marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 14 }}>
        Last 7 days
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120, position: 'relative' }}>
        {goal && (
          <div
            title={`Goal ${goal.toLocaleString()} kcal`}
            style={{
              position: 'absolute', left: 0, right: 0, bottom: `${(goal / max) * 100}%`,
              borderTop: '1px dashed var(--line)', pointerEvents: 'none',
            }}
          />
        )}
        {trend.map((t) => (
          <div key={t.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
            <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{t.total > 0 ? t.total.toLocaleString() : ''}</div>
            <div
              title={`${t.total.toLocaleString()} kcal`}
              style={{
                width: '100%', maxWidth: 38,
                height: `${Math.max(2, (t.total / max) * 100)}%`,
                background: t.isToday ? 'var(--gradient)' : 'var(--surface-2)',
                borderRadius: 8,
              }}
            />
            <div style={{ fontSize: 11, color: t.isToday ? 'var(--ink)' : 'var(--ink-3)', fontWeight: t.isToday ? 700 : 400 }}>
              {t.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function History({ days, goal }: { days: DaySummary[]; goal?: number }) {
  if (days.length === 0) return null;
  return (
    <section>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-2)', margin: '0 0 12px' }}>History</h2>
      {days.map((d) => (
        <div
          key={d.key}
          style={{
            background: 'var(--card)', borderRadius: 18, padding: 20,
            boxShadow: '0 0 0 1px var(--line)', marginBottom: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontWeight: 700, color: 'var(--ink)' }}>{d.label}</span>
            <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>
              <strong style={{ color: 'var(--ink)' }}>{d.total.toLocaleString()}</strong>
              {goal ? ` / ${goal.toLocaleString()}` : ''} kcal
            </span>
          </div>
          {d.meals.map((m) => <MealRow key={m.uid} meal={m} />)}
        </div>
      ))}
    </section>
  );
}

function MealRow({ meal }: { meal: Meal }) {
  const time = new Date(meal.consumedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
      {meal.hasPhoto && <MealPhoto mealUid={meal.uid} />}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 11, fontWeight: 600, color: 'var(--ink-3)',
              textTransform: 'capitalize', background: 'var(--surface-2)',
              padding: '1px 7px', borderRadius: 6, whiteSpace: 'nowrap',
            }}
          >
            {meal.mealType}
          </span>
          <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{meal.name}</span>
        </div>
        {meal.items.length > 0 && (
          <div style={{ marginTop: 3, fontSize: 12, color: 'var(--ink-3)' }}>
            {meal.items.map((i) => i.amount ? `${i.name} (${i.amount})` : i.name).join(' · ')}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: 12 }}>
        <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{meal.kcal.toLocaleString()}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{time}</div>
      </div>
    </div>
  );
}

// MealPhoto lazily resolves a short-lived signed GET URL for the meal's photo
// and renders a thumbnail. Renders nothing if the photo is missing or storage
// is disabled — the signed URL is self-authenticating, so the <img> needs no
// auth header.
function MealPhoto({ mealUid }: { mealUid: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<{ url: string }>(`/api/photos/view-url?mealUid=${encodeURIComponent(mealUid)}`)
      .then((r) => { if (!cancelled) setUrl(r.url); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [mealUid]);

  if (failed) return null;
  return (
    <div
      style={{
        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
        background: 'var(--surface-2)', overflow: 'hidden',
      }}
    >
      {url && (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </div>
  );
}

function Card({ children }: { children: ComponentChildren }) {
  return (
    <div style={{ background: 'var(--card)', borderRadius: 24, padding: 32, boxShadow: 'var(--shadow-card)' }}>
      {children}
    </div>
  );
}

function Muted({ children }: { children: ComponentChildren }) {
  return <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>{children}</p>;
}

function fmtAgo(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'recently';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return d.toLocaleDateString();
}
